class StoreAvailability extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.allStores = [];
    this.userLocation = null;
    this.currentVariantId = null;
  }

  /* =============================================
      Location Data Normalization Utility
  ============================================= */

  /**
   * Normalizes location data from different APIs into a consistent format
   * @param {Object} rawData - Raw location data from IP API or Geo API
   * @param {string} source - Source of the data ('ip', 'geo', 'manual', 'gps')
   * @returns {Object} Normalized location object
   */
  normalizeLocationData(rawData, source = 'unknown') {
    if (!rawData) return null;

    // Handle IP API format (ipapi.co)
    if (source === 'ip' || (rawData.latitude && rawData.longitude)) {
      return {
        lat: rawData.latitude,
        lng: rawData.longitude,
        city: rawData.city || '',
        state: rawData.region || rawData.state || '',
        stateCode: rawData.region_code || rawData.state_code || '',
        country: rawData.country_name || rawData.country || '',
        countryCode: rawData.country_code || rawData.country || '',
        postal: rawData.postal || '',
        source: source,
        accuracy: rawData.accuracy || 'city',
        formatted: this.formatLocationDisplay(rawData.city, rawData.region || rawData.state, rawData.country_name || rawData.country)
      };
    }

    // Handle Geo API format (geoapify.com)
    if (source === 'geo' || (rawData.lat && (rawData.lon || rawData.lng))) {
      return {
        lat: rawData.lat,
        lng: rawData.lon || rawData.lng,
        city: rawData.city || '',
        state: rawData.state || rawData.region || '',
        stateCode: rawData.state_code || rawData.region_code || '',
        country: rawData.country || '',
        countryCode: rawData.country_code || '',
        postal: rawData.postal || '',
        source: source,
        accuracy: rawData.accuracy || 'city',
        formatted: rawData.formatted || this.formatLocationDisplay(rawData.city, rawData.state, rawData.country)
      };
    }

    // Handle manual/existing normalized format
    if (rawData.lat && rawData.lng) {
      return {
        lat: rawData.lat,
        lng: rawData.lng,
        city: rawData.city || '',
        state: rawData.state || rawData.region || '',
        stateCode: rawData.stateCode || rawData.state_code || '',
        country: rawData.country || '',
        countryCode: rawData.countryCode || rawData.country_code || '',
        postal: rawData.postal || '',
        source: rawData.source || source,
        accuracy: rawData.accuracy || 'city',
        formatted: rawData.formatted || rawData.label || this.formatLocationDisplay(rawData.city, rawData.state || rawData.region, rawData.country)
      };
    }

    return null;
  }

  /**
   * Creates a consistent display format for location
   */
  formatLocationDisplay(city, state, country) {
    const parts = [];
    if (city) parts.push(city);
    if (state) parts.push(state);
    if (country && country !== state) parts.push(country);
    return parts.join(', ') || 'Unknown location';
  }

  connectedCallback() {
    this.loadSelectedVariantId();
    this.collectStores();
    this.bindEvents();
    this.initHeaderHeightTracking();
    
    // Expose instance globally for external helpers
    window.storeAvailability = this;
    
    // Auto-detect location on modal open
    this.autoDetectLocation();
  }

  loadSelectedVariantId() {
    // Load selected variant ID from Liquid-generated script
    const variantIdScript = this.querySelector('[data-selected-variant-id]');
    if (variantIdScript) {
      try {
        this.currentVariantId = JSON.parse(variantIdScript.textContent);
      } catch (e) {
        console.warn('Failed to parse selected variant ID:', e);
      }
    }

    // Fallback: try to get from URL parameters
    if (!this.currentVariantId) {
      const urlParams = new URLSearchParams(window.location.search);
      const variantParam = urlParams.get('variant');
      if (variantParam) {
        this.currentVariantId = parseInt(variantParam);
      }
    }

    // Last resort: try to get from global theme variant data
    if (!this.currentVariantId && window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.selectedVariantId) {
      this.currentVariantId = window.ShopifyAnalytics.meta.selectedVariantId;
    }
  }

  collectStores() {
    this.allStores = Array.from(this.querySelectorAll('.store-item'));
  }

  bindEvents() {
    const trigger = this.querySelector('.store-availability-trigger');
    const closeBtn = this.querySelector('.modal-close');
    const overlay = this.querySelector('.modal-overlay');

    trigger?.addEventListener('click', () => this.openModal());
    closeBtn?.addEventListener('click', () => this.closeModal());
    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) this.closeModal();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.closeModal();
    });
  }

  openModal() {
    const modal = this.querySelector('.store-availability-modal');
    if (modal) modal.hidden = false;
    this.isOpen = true;
    document.body.style.overflow = 'hidden';

    // Show only the selected variant blocks
    this.showVariantBlocks(this.currentVariantId);

    if (this.userLocation) {
      this.calculateDistances();
      this.sortStoresByDistance();
    }
  }

  closeModal() {
    const modal = this.querySelector('.store-availability-modal');
    if (modal) modal.hidden = true;
    this.isOpen = false;
    document.body.style.overflow = '';
  }

  /**
   * Initialize dynamic header height tracking
   * Watches for changes in header height and announcement bar visibility
   */
  initHeaderHeightTracking() {
    const header = document.querySelector('.site-header-wrapper, .site-header');
    const announcementBar = document.querySelector('.announcement-bar');
    
    if (!header) return;

    // Store observers for cleanup if needed
    this.headerObservers = {
      resize: null,
      intersection: null
    };

    // Track announcement bar visibility state
    this.isAnnouncementBarVisible = false;

    // Set initial height
    this.updateHeaderHeight(header, announcementBar);

    // Watch for header height changes (dropdown menus, responsive changes)
    this.headerObservers.resize = new ResizeObserver(() => {
      this.updateHeaderHeight(header, announcementBar);
    });
    this.headerObservers.resize.observe(header);

    // Watch for announcement bar visibility changes (scroll, hide/show)
    if (announcementBar) {
      // Also observe announcement bar size changes
      this.headerObservers.resize.observe(announcementBar);

      // Track visibility with IntersectionObserver
      this.headerObservers.intersection = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const wasVisible = this.isAnnouncementBarVisible;
          this.isAnnouncementBarVisible = entry.isIntersecting && entry.intersectionRatio > 0;
          
          // Only update if visibility actually changed
          if (wasVisible !== this.isAnnouncementBarVisible) {
            this.updateHeaderHeight(header, announcementBar);
          }
        });
      }, {
        threshold: [0, 0.1, 0.5, 1],
        rootMargin: '0px'
      });
      this.headerObservers.intersection.observe(announcementBar);
    }
  }

  /**
   * Update CSS custom properties for header height
   * @param {HTMLElement} header - The header element
   * @param {HTMLElement} announcementBar - The announcement bar element
   */
  updateHeaderHeight(header, announcementBar) {
    if (!header) return;

    let headerHeight = header.offsetHeight;
    let totalHeight = headerHeight;

    // Check if announcement bar exists and is visible
    if (announcementBar) {
      const announcementBarHeight = announcementBar.offsetHeight;
      const isVisible = this.isAnnouncementBarVisible !== false && 
                       announcementBarHeight > 0 && 
                       window.getComputedStyle(announcementBar).display !== 'none' &&
                       window.getComputedStyle(announcementBar).visibility !== 'hidden';

      if (isVisible) {
        totalHeight += announcementBarHeight;
      }
    }

    // Set CSS custom properties
    document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
    document.documentElement.style.setProperty('--header-height-mobile', `${headerHeight}px`);
  }

  /**
   * Cleanup observers when component is removed
   */
  disconnectedCallback() {
    if (this.headerObservers) {
      if (this.headerObservers.resize) {
        this.headerObservers.resize.disconnect();
      }
      if (this.headerObservers.intersection) {
        this.headerObservers.intersection.disconnect();
      }
    }
  }
  
  autoDetectLocation() {
    const cachedLocation = this.getCachedLocation();
    if (cachedLocation) {
      this.userLocation = this.normalizeLocationData(cachedLocation);
      this.showLocationFromCache(this.userLocation);
      this.calculateDistances();
      this.sortStoresByDistance();
      return;
    }
    this.detectLocationByIP();
  }

  getCachedLocation() {
    try {
      const cached = localStorage.getItem('store_location_data');
      if (!cached) return null;

      const data = JSON.parse(cached);
      const now = Date.now();
      
      // Cache expires after 24 hours
      if (now - data.timestamp > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('store_location_data');
        return null;
      }
      return data.location;
    } catch (e) {
      localStorage.removeItem('store_location_data');
      return null;
    }
  }

  setCachedLocation(location) {
    try {
      const data = {
        location: location,
        timestamp: Date.now()
      };
      localStorage.setItem('store_location_data', JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to cache location data');
    }
  }

  async detectLocationByIP() {
    const locationText = this.querySelector('[data-location-text]');
    if (locationText) locationText.textContent = 'Detecting your location...';

    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      if (data.latitude && data.longitude) {
        const location = this.normalizeLocationData(data, 'ip');
        if (location) {
          this.userLocation = location;
          this.setCachedLocation(location);
          this.showLocationResult(location);
          this.calculateDistances();
          this.sortStoresByDistance();
        } else {
          throw new Error('Failed to normalize location data');
        }
      } else {
        this.showManualLocation();
        throw new Error('Invalid location data from IP API');
      }
    } catch (error) {
      console.warn('IP geolocation failed:', error);
      this.showManualLocation();
    }
  }

  showManualLocation() {
    const locationStatus = this.querySelector('[data-location-status]');
    const locationResult = this.querySelector('[data-location-result]');
    const manualLocation = this.querySelector('[data-manual-location]');
    
    if(locationStatus) locationStatus.hidden = true;
    if(locationResult) locationResult.hidden = true;
    if(manualLocation) manualLocation.hidden = false;
    
    const input = this.querySelector('[data-location-input]');
    input?.focus();
  }

  showLocationFromCache(location) {
    const locationStatus = this.querySelector('[data-location-status]');
    const locationResult = this.querySelector('[data-location-result]');
    const locationText = this.querySelector('[data-location-text]');
    
    if(locationStatus) locationStatus.hidden = true;
    if(locationResult) locationResult.hidden = false;
    
    if (locationText) {
      const locationDisplay = location.formatted || this.formatLocationDisplay(location.city, location.state, location.country);
      locationText.textContent = `${locationDisplay}`;
    }
  }

  showLocationResult(location) {
    const locationStatus = this.querySelector('[data-location-status]');
    const locationResult = this.querySelector('[data-location-result]');
    const locationText = this.querySelector('[data-location-text]');
    const manualLocation = this.querySelector('[data-manual-location]');

    if(locationStatus) locationStatus.hidden = true;
    if(locationResult) locationResult.hidden = false;
    if(manualLocation) manualLocation.hidden = true;

    if (locationText) {
      const locationDisplay = location.formatted || this.formatLocationDisplay(location.city, location.state, location.country);
      let accuracyInfo = '';
      
      if (location.source === 'ip') {
        accuracyInfo = ' ';
      } else if (location.source === 'geo') {
        accuracyInfo = ' ';
      }
      
      locationText.textContent = locationDisplay + accuracyInfo;
    }
  }

    calculateDistances() {
      if (!this.userLocation) return;
     
    
    this.allStores.forEach(store => {
      const storeLat = parseFloat(store.dataset.storeLat);
      const storeLng = parseFloat(store.dataset.storeLng);
      
      if (storeLat && storeLng) {
        let distance = this.calculateDistance(
          this.userLocation.lat,
          this.userLocation.lng,
          storeLat,
          storeLng
        );
      

        
        // Treat very small distances as exact matches (0 km)
        // This handles floating-point precision issues
        if (distance < 0.01) { // Less than 10 meters
          distance = 0;
        }
        
        store.dataset.storeDistance = distance.toFixed(1);
        this.updateStoreDistanceDisplay(store, distance);
      }
    });
  }

  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
      
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  updateStoreDistanceDisplay(store, distance) {
   
    const storeName = store.querySelector('.store-name');
    let existingDistance = store.querySelector('.store-distance');
    
    if (!existingDistance) {
        existingDistance = document.createElement('div');
        existingDistance.className = 'store-distance';
        storeName?.insertAdjacentElement('afterend', existingDistance);
    }
    
    // Special display for exact location matches
    if (distance === 0) {
      existingDistance.innerHTML = `
        <span class="distance-icon">📍</span>
        <span>At this location</span>
      `;
      existingDistance.classList.add('distance-exact-match');
    } else {
      existingDistance.innerHTML = `
        <span class="distance-icon">📍</span>
        <span>${distance.toFixed(1)}km away</span>
      `;
      existingDistance.classList.remove('distance-exact-match');
    }
  }

  sortStoresByDistance() {
    if (!this.userLocation) return;
    
    const container = this.querySelector('.stores-grid');
    const storesWithDistance = this.allStores
      .map(store => {
        const distance = parseFloat(store.dataset.storeDistance);
        const storeName = store.dataset.storeName || store.querySelector('.store-name')?.textContent?.trim() || '';
        
        return {
          element: store,
          distance: isNaN(distance) ? Infinity : distance,
          storeName: storeName.toLowerCase()
        };
      })
      .sort((a, b) => {
        // Priority 1: Exact geolocation match (distance == 0) comes first
        if (a.distance === 0 && b.distance !== 0) return -1;
        if (b.distance === 0 && a.distance !== 0) return 1;
        
        // Priority 2: Sort by distance (ascending)
        if (a.distance !== b.distance) {
          return a.distance - b.distance;
        }
        
        // Priority 3: Fallback - alphabetical by store name for same distance
        return a.storeName.localeCompare(b.storeName);
      });
    
    // Reorder DOM elements
    storesWithDistance.forEach(({ element }) => {
      container?.appendChild(element);
    });
  }

  setManualLocation(rawLocation) {
    if (!rawLocation) return;
    
    const location = this.normalizeLocationData(rawLocation, rawLocation.source || 'manual');
    if (!location || !location.lat || !location.lng) return;

    this.userLocation = location;
    this.setCachedLocation(location);
    this.showLocationResult(location);
    this.calculateDistances();
    this.sortStoresByDistance();
  }

  /* =============================================
      Variant Visibility Control (Liquid-first approach)
  ============================================= */

  /**
   * Show variant blocks for a specific variant ID
   * Hides all other variant blocks
   */
  showVariantBlocks(variantId) {
    if (!variantId) return;

    // Find all elements with data-variant-id attribute
    const allVariantBlocks = this.querySelectorAll('[data-variant-id]');
    
    allVariantBlocks.forEach(block => {
      const blockVariantId = parseInt(block.dataset.variantId);
      
      if (blockVariantId === variantId) {
        // Show blocks matching the selected variant
        block.style.display = '';
      } else {
        // Hide blocks not matching the selected variant
        block.style.display = 'none';
      }
    });

    // Update store stock data attributes for quick reference
    this.updateStoreStockAttributes(variantId);
  }

  /**
   * Update store data attributes for the selected variant
   * This helps with distance sorting and other functionality
   */
  updateStoreStockAttributes(variantId) {
    this.allStores.forEach(store => {
      const variantBlock = store.querySelector(`[data-variant-id="${variantId}"]`);
      if (variantBlock && variantBlock.dataset.stock !== undefined) {
        store.dataset.stock = variantBlock.dataset.stock;
      }
    });
  }

  /**
   * Set the current variant and update visibility
   */
  setVariant(variantId) {
    this.currentVariantId = parseInt(variantId);
    if (this.isOpen) {
      this.showVariantBlocks(this.currentVariantId);
    }
  }
}

customElements.define('store-availability', StoreAvailability);

/* =============================================
    Autocomplete for manual location input using Geoapify
============================================= */

(function () {
  const input = document.getElementById('manual-location-input');
  const suggestionsList = document.getElementById('autocomplete-suggestions');
  if (!input || !suggestionsList) return;

  const API_KEY = '4162e07f6df940bb90d409f6ea374407'; // TODO: Replace with your Geoapify key

  /**
   * Get current site language for API requests
   * @returns {string} Language code ('en' or 'fr')
   */
  function getCurrentLanguage() {
    // Try Shopify.locale first (most reliable for Shopify themes)
    if (window.Shopify && window.Shopify.locale) {
      const locale = window.Shopify.locale.toLowerCase();
      return locale.startsWith('fr') ? 'fr' : 'en';
    }
    
    // Fallback to document language
    const docLang = document.documentElement.lang?.toLowerCase() || 'en';
    return docLang.startsWith('fr') ? 'fr' : 'en';
  }

  /**
   * Build optimized search parameters for Canadian addresses and postal codes
   * @param {string} searchText - User input text
   * @param {string} language - Language code
   * @returns {string} Complete API URL
   */
  function buildSearchURL(searchText, language) {
    const baseURL = 'https://api.geoapify.com/v1/geocode/autocomplete';
    const params = new URLSearchParams({
      text: searchText,
      lang: language,
      limit: '6', // Slightly more results for better coverage
      filter: 'countrycode:ca', // Restrict to Canada only
      format: 'json',
      apiKey: API_KEY
    });

    // If input looks like a postal code (Canadian format), add postal code bias
    const isPostalCode = /^[A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d$/.test(searchText.replace(/\s/g, ''));
    if (isPostalCode) {
      params.append('type', 'postcode');
    }

    return `${baseURL}?${params.toString()}`;
  }

  let debounceTimeout;
  input.addEventListener('input', () => {
    clearTimeout(debounceTimeout);
    const value = input.value.trim();
    
    if (value.length < 2) { // Reduced from 3 to 2 for postal codes (e.g., "H2")
      suggestionsList.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      return;
    }
    
    debounceTimeout = setTimeout(() => {
      const currentLang = getCurrentLanguage();
      const searchURL = buildSearchURL(value, currentLang);
      
      fetch(searchURL)
        .then(res => res.json())
        .then(data => {
          const results = data.results || [];
          if (results.length > 0) {
            suggestionsList.innerHTML = results
              .map(place => {
                const normalized = window.storeAvailability?.normalizeLocationData(place, 'geo');
                const displayText = normalized?.formatted || place.formatted;
                
                // Enhance display for postal codes vs addresses
                let enhancedDisplay = displayText;
                if (place.result_type === 'postcode' && place.postcode) {
                  enhancedDisplay = `${place.postcode} - ${displayText}`;
                }
                
                return `<li role="option" tabindex="0" data-lat="${normalized?.lat || place.lat}" data-lng="${normalized?.lng || place.lon}" data-formatted="${displayText}">${enhancedDisplay}</li>`;
              })
              .join('');
            input.setAttribute('aria-expanded', 'true');
          } else {
            suggestionsList.innerHTML = '';
            input.setAttribute('aria-expanded', 'false');
          }
        })
        .catch(() => {
          suggestionsList.innerHTML = '';
          input.setAttribute('aria-expanded', 'false');
        });
    }, 250); // Slightly faster response for better UX
  });

  input.addEventListener('blur', () => {
    setTimeout(() => {
      suggestionsList.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    }, 150);
  });

  input.addEventListener('keydown', (e) => {
    const items = suggestionsList.querySelectorAll('li');
    if (items.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[0].focus();
    } else if (e.key === 'Escape') {
      suggestionsList.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    }
  });

  suggestionsList.addEventListener('keydown', (e) => {
    const items = Array.from(suggestionsList.querySelectorAll('li'));
    const currentIndex = items.indexOf(e.target);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % items.length;
      items[nextIndex].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
      items[prevIndex].focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.target.click();
    } else if (e.key === 'Escape') {
      suggestionsList.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.focus();
    }
  });

  suggestionsList.addEventListener('click', (e) => {
    const item = e.target.closest('li');
    if (!item) return;
    const lat = parseFloat(item.dataset.lat);
    const lng = parseFloat(item.dataset.lng);
    const formatted = item.dataset.formatted || item.textContent.trim();

    input.value = formatted;
    suggestionsList.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');

    if (window.storeAvailability && typeof window.storeAvailability.setManualLocation === 'function') {
      const rawLocation = {
        lat: lat,
        lng: lng,
        formatted: formatted,
        source: 'geo'
      };
      window.storeAvailability.setManualLocation(rawLocation);
    }
  });
})();

// Listen for variant changes from Shopify theme
window.addEventListener('shopify-variants:switch-variant', ({ detail }) => {
  const { variant } = detail;
  
  if (variant && window.storeAvailability) {
    window.storeAvailability.setVariant(variant.id);
  }
});

// Alternative event listener for variant changes (some themes use different events)
document.addEventListener('variant:changed', ({ detail }) => {
  const { variant } = detail;
  
  if (variant && window.storeAvailability) {
    window.storeAvailability.setVariant(variant.id);
  }
}); 